import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import type {
  IStudentAlertTarget,
  PaymentsStatusFilter,
  StudentTabId,
} from 'core/utils/studentAlerts';
import {
  applyAlertNavigation,
  encode,
  parse,
  IStudentDeepLinkState,
} from 'core/utils/studentDeepLink';

/**
 * React wrapper around the pure `core/utils/studentDeepLink` encode/parse logic.
 *
 * Owns the Student card deep-link view state (`?tab/?pa/?ps`): it reads
 * `location.search`, parses it into an {@link IStudentDeepLinkState}, and exposes
 * setters that re-encode the next state and persist it through the router. Port
 * of `useGroupDeepLinkState`.
 *
 * Writes use `history.replace` (not `push`) so deep-link changes do not pollute
 * the browser back stack, matching the existing `?tab` behavior. Payments
 * filters are encoded only when their value actually changes (Req 20.2) — the
 * setters no-op when the next state equals the current one.
 *
 * `applyAlert` runs the util's `applyAlertNavigation`, which **replaces** the tab
 * + Payments filter from a single alert target rather than merging (Req 6.7).
 */
export interface IUseStudentDeepLinkState {
  /** The parsed deep-link state, memoized against `location.search`. */
  state: IStudentDeepLinkState;
  /** Switch the active tab. */
  setTab: (tab: StudentTabId) => void;
  /** Set the Payments-tab agreement filter (`all` clears it). */
  setPaymentsAgreement: (agreement: string) => void;
  /** Set the Payments-tab status filter. */
  setPaymentsStatus: (status: PaymentsStatusFilter) => void;
  /** Navigate from an alert target, replacing (never merging) tab + filters. */
  applyAlert: (target: IStudentAlertTarget) => void;
}

/** Shallow equality for the deep-link state — avoids redundant history writes. */
const isSameState = (
  a: IStudentDeepLinkState,
  b: IStudentDeepLinkState,
): boolean =>
  a.tab === b.tab &&
  a.paymentsAgreement === b.paymentsAgreement &&
  a.paymentsStatus === b.paymentsStatus;

/** Hook owning the Student card URL deep-link state. */
export const useStudentDeepLinkState = (): IUseStudentDeepLinkState => {
  const history = useHistory();
  const location = useLocation();

  const state = useMemo<IStudentDeepLinkState>(() => parse(location.search), [
    location.search,
  ]);

  /** Encode the next state and persist it via `history.replace`, if changed. */
  const commit = useCallback(
    (next: IStudentDeepLinkState) => {
      if (isSameState(next, state)) return;
      const params = encode(next);
      history.replace({ ...location, search: params.toString() });
    },
    [history, location, state],
  );

  const setTab = useCallback((tab: StudentTabId) => commit({ ...state, tab }), [
    commit,
    state,
  ]);

  const setPaymentsAgreement = useCallback(
    (paymentsAgreement: string) => commit({ ...state, paymentsAgreement }),
    [commit, state],
  );

  const setPaymentsStatus = useCallback(
    (paymentsStatus: PaymentsStatusFilter) =>
      commit({ ...state, paymentsStatus }),
    [commit, state],
  );

  const applyAlert = useCallback(
    (target: IStudentAlertTarget) =>
      commit(applyAlertNavigation(state, target)),
    [commit, state],
  );

  return {
    state,
    setTab,
    setPaymentsAgreement,
    setPaymentsStatus,
    applyAlert,
  };
};
